import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useNavigate, Link } from 'react-router-dom'
import { AxiosError } from 'axios'
import { useTranslation } from 'react-i18next'

import { getLoginSchema, LoginFormData } from '@/schemas/auth'
import { ApiError } from '@/services/auth'
import { useAuthStore } from '@/stores/auth-store'
import api from '@/services/api'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Alert, AlertDescription } from '@/components/ui/alert'

const DEVICE_TOKEN_KEY = 'archon_device_token'

export function LoginPage() {
  const { t } = useTranslation('auth')
  const navigate = useNavigate()
  const setAuth = useAuthStore((state) => state.setAuth)
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(getLoginSchema()),
  })

  const onSubmit = async (data: LoginFormData) => {
    setError(null)
    setIsLoading(true)

    try {
      const deviceToken = localStorage.getItem(DEVICE_TOKEN_KEY)

      const response = await api.post('/auth/login', data, {
        headers: deviceToken ? { 'X-Device-Token': deviceToken } : undefined,
      })

      const loginResponse = response.data

      if (loginResponse.requires_2fa) {
        navigate('/verify-2fa', {
          state: { temporaryToken: loginResponse.temporary_token },
        })
        return
      }

      if (loginResponse.user && loginResponse.access_token && loginResponse.refresh_token) {
        setAuth(loginResponse.user, loginResponse.access_token, loginResponse.refresh_token)
        navigate('/dashboard')
      }
    } catch (err) {
      const axiosError = err as AxiosError<ApiError>

      if (axiosError.response?.status === 401) {
        setError(t('errors.invalidCredentials'))
      } else if (axiosError.response?.status === 429) {
        setError(
          axiosError.response.data?.detail || t('errors.tooManyAttempts')
        )
      } else if (axiosError.response?.status === 403) {
        setError(axiosError.response.data?.detail || t('errors.accessDenied'))
      } else {
        setError(t('errors.loginError'))
      }
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <CardTitle className="text-2xl font-bold">{t('login.title')}</CardTitle>
          <CardDescription>
            {t('login.subtitle')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <div className="space-y-2">
              <Label htmlFor="email">{t('login.email')}</Label>
              <Input
                id="email"
                type="email"
                placeholder={t('login.emailPlaceholder')}
                {...register('email')}
                disabled={isLoading}
              />
              {errors.email && (
                <p className="text-sm text-destructive">
                  {errors.email.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">{t('login.password')}</Label>
              <Input
                id="password"
                type="password"
                placeholder={t('login.passwordPlaceholder')}
                {...register('password')}
                disabled={isLoading}
              />
              {errors.password && (
                <p className="text-sm text-destructive">
                  {errors.password.message}
                </p>
              )}
            </div>

            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading ? t('login.signingIn') : t('login.signIn')}
            </Button>
          </form>
        </CardContent>
        <CardFooter className="flex justify-center">
          <Link
            to="/forgot-password"
            className="text-sm text-muted-foreground hover:text-primary"
          >
            {t('login.forgotPassword')}
          </Link>
        </CardFooter>
      </Card>
    </div>
  )
}
